package com.swetdot.swetgram.ui.design

import androidx.compose.animation.Crossfade
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier

/** Stable phases for a screen whose data can be loaded and refreshed independently. */
enum class SwetContentPhase {
    Loading,
    Content,
    Empty,
    Error,
}

/**
 * Cached content always wins over a refresh error. The error can be shown as a banner without
 * replacing useful data; [Error] is reserved for a terminal failure with nothing cached.
 */
fun swetContentPhase(
    hasContent: Boolean,
    loadedOnce: Boolean,
    hasError: Boolean,
): SwetContentPhase = when {
    hasContent -> SwetContentPhase.Content
    !loadedOnce -> SwetContentPhase.Loading
    hasError -> SwetContentPhase.Error
    else -> SwetContentPhase.Empty
}

/**
 * Crossfades complete screen states inside one fixed viewport. During the overlap only the new
 * phase exposes semantics, preventing duplicated controls from being announced or activated.
 */
@Composable
fun SwetStateHost(
    phase: SwetContentPhase,
    modifier: Modifier = Modifier,
    content: @Composable (SwetContentPhase) -> Unit,
) {
    Box(modifier = modifier) {
        Crossfade(
            targetState = phase,
            animationSpec = swetTween(Motion.fast, SwetEasing.enter),
            label = "swetContentPhase",
        ) { renderedPhase ->
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .swetTransitionTarget(renderedPhase == phase),
            ) {
                content(renderedPhase)
            }
        }
    }
}
